import { filter, get, noop } from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { filterToValidAttributes } from '@zocdoc/dom-property';
import { navy, grey25, white } from '@zocdoc/branding/colors';
import { sharpSansBold } from '../typography';
import { breakpoint } from '../grid';
import scrollToElement from 'scroll-to-element';
import { SCROLL_ANIMATION_DURATION } from './constants';

export default class TabsView extends React.PureComponent {
    static propTypes = {
        onTabClick: PropTypes.func,
    };

    static defaultProps = {
        onTabClick: noop,
    };

    state = {
        active: 0,
    };

    render() {
        const {
            children, style, isSticky, innerRef, tabHeight, ...props
        } = this.props;
        const validChildren = filter(children, child => { return get(child, 'props.title', null); });
        return (
            <div
                data-test="tabs"
                {...filterToValidAttributes(props)}
                ref={node => { this.$root = node; typeof innerRef === 'function' && innerRef(node, this.setTabIndex); }}
            >
                <TabLinkHolder
                    data-test="tab-link-holder"
                    role="tablist"
                    style={style}
                >
                    {React.Children.map(validChildren, this.mapTabToLink)}
                </TabLinkHolder>
                <TabContent
                    isSticky={isSticky}
                    offsetTop={tabHeight}
                >
                    {React.Children.map(validChildren, this.mapTabToPane)}
                </TabContent>
            </div>
        );
    }

    focusPane = (tabIndex = this.state.active) => {
        const el = this.$root.querySelector(`[data-tab-pane-index="${tabIndex}"]`);
        if (el && el.offsetHeight) {
            const lastTop = window.pageYOffset;
            if (tabIndex > 0 && this.$root.querySelector('[data-tab-pane-index="0"]').offsetHeight > 0) {
                scrollToElement(el, { duration: SCROLL_ANIMATION_DURATION });
                setTimeout(el.focus(), SCROLL_ANIMATION_DURATION);
            } else {
                el.focus();
                // This prevents unwanted scrolling from the el.focus() call above, while allowing the focus to move
                window.scrollTo(0, lastTop);
            }
        }
    }

    tabClick = ev => {
        ev.preventDefault();
        this.props.onTabClick(ev);

        const idx = parseInt(ev.currentTarget.getAttribute('data-tab-control-index'), 10);

        if (this.state.active === idx) {
            this.focusPane();
        } else {
            this.setState({ active: idx });
            this.focusPane(idx);
        }
    }

    mapTabToLink = (child, idx) => {
        const active = idx === this.state.active;

        return (
            <TabLinkItem
                active={active}
                data-tab-control-index={idx}
                data-test={`tab-link-${idx}`}
                onClick={this.tabClick}
                role="tab"
                tabIndex="0"
            >
                {child.props.title}
            </TabLinkItem>
        );
    }

    mapTabToPane = (child, idx) => {
        const active = idx === this.state.active;
        return (
            <Pane
                active={active}
                data-tab-pane-index={idx}
                data-test="tab-panel"
                role="tabpanel"
                tabIndex="-1"
            >
                {child}
            </Pane>
        );
    }

    setTabIndex = tabIndex => {
        const { children } = this.props;
        if (!tabIndex
            || tabIndex > (children.length - 1)
            || tabIndex < 0) {
            return;
        }

        this.setState({ active: tabIndex });
        this.focusPane(tabIndex);
    }
}

const TabLinkHolder = styled.div`
    display: flex;
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
`;

/**
 * Per design, at 1024px wide, the first tab should always display as active,
 * and later tabs should always display as inactive.
 */
export const TabLinkItem = styled.button`
    ${sharpSansBold(13, 24)};
    background-color: transparent;
    border-top: none;
    border-left: none;
    border-right: none;
    border-bottom: 4px solid ${p => p.active ? p.theme.components.button.inverted.bgColor : grey25};
    text-align: center;
    letter-spacing: 0.1px;
    flex-grow: 1;
    flex-basis: 0;
    color: ${navy};
    padding: 8px;
    cursor: pointer;

    ${breakpoint.small} {
        font-size: 15px;
        padding: 12px;
    }

    ${breakpoint.medium} {
        border-top: 1px solid ${grey25};
        border-bottom: 4px solid ${grey25};

        &:hover {
            background-color: ${p => p.theme.components.button.inverted.bgColor};
            border-bottom: 4px solid ${p => p.theme.components.button.inverted.bgColor};
            color: ${white};
        }

        &:first-child {
            border-bottom: 4px solid ${p => p.theme.components.button.inverted.bgColor};
            background-color: transparent;
            color: ${navy};
        }
    }
`;

/**
 * Per design, at 1024px wide, the tabs should stop behaving like tabs
 * and start behaving like links.
 */
const Pane = styled.div`
    display: ${props => props.active ? 'block' : 'none'};
    outline: 0;

    ${breakpoint.medium} {
        display: block;
    }
`;

export const TabContent = styled.div`
    padding-top: ${props => props.isSticky ? props.offsetTop : '0'}px;
`;
